import type { ReactNode } from 'react';
import { parseAnswer, parseInline, plainCell } from '@/lib/answer';

// One Dr Cleanstein answer, laid out for a phone. Tables never show pipes: each row is
// a small card, the first column as its title and the other columns as "Label: value"
// lines underneath. Everything is React text, so an answer can never become HTML.

function Inline({ text }: { text: string }) {
  const parts: ReactNode[] = parseInline(text).map((p, i) => {
    if (p.kind === 'bold') return <strong key={i}>{p.text}</strong>;
    if (p.kind === 'italic') return <em key={i}>{p.text}</em>;
    // A bare long address reads as its site and "…" on one line (mobile review, 18 Oct).
    if (p.kind === 'link') return <a key={i} href={p.href} target="_blank" rel="noopener noreferrer">{p.text === p.href && p.text.length > 40 ? shortLink(p.href) : p.text}</a>;
    return p.text;
  });
  return <>{parts}</>;
}

function shortLink(href: string): string {
  try {
    const u = new URL(href);
    const path = u.pathname.split('/').filter(Boolean)[0];
    return `${u.hostname.replace(/^www\./, '')}${path ? `/${path}` : ''}…`;
  } catch {
    return href.slice(0, 40) + '…';
  }
}

/** tables: a real table (a computer), instead of one card a row (a phone). */
export function DrcAnswer({ text, tables = false }: { text: string; tables?: boolean }) {
  return (
    <div className="chats-drc-answer">
      {parseAnswer(text).map((b, idx) => {
        if (b.kind === 'h') return <p key={idx} className="chats-drc-answer-h"><Inline text={b.text} /></p>;
        if (b.kind === 'ul' || b.kind === 'ol') {
          const items = b.items.map((it, j) => (
            <li key={j}>
              <Inline text={it} />
              {(b.subs[j]?.length ?? 0) > 0 && <ul>{b.subs[j]!.map((s, k) => <li key={k}><Inline text={s} /></li>)}</ul>}
            </li>
          ));
          return b.kind === 'ol' ? <ol key={idx} start={b.start}>{items}</ol> : <ul key={idx}>{items}</ul>;
        }
        if (b.kind === 'table' && tables) {
          const { headers, rows } = b.table;
          return (
            <div key={idx} className="chats-drc-table-wrap">
              <table className="chats-drc-table">
                <thead><tr>{headers.map((h, k) => <th key={k}>{plainCell(h)}</th>)}</tr></thead>
                <tbody>{rows.map((row, j) => <tr key={j}>{row.map((cell, k) => <td key={k}><Inline text={cell} /></td>)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        if (b.kind === 'table') {
          const { headers, rows } = b.table;
          return (
            <div key={idx} className="chats-drc-cards">
              {rows.map((row, j) => (
                <div key={j} className="chats-drc-card">
                  <div className="chats-drc-card-title"><Inline text={row[0] ?? ''} /></div>
                  {row.slice(1).map((cell, k) => (cell.trim() ? (
                    <div key={k} className="chats-drc-card-line">
                      <span className="chats-drc-card-label">{plainCell(headers[k + 1] ?? '')}</span>
                      <span><Inline text={cell} /></span>
                    </div>
                  ) : null))}
                </div>
              ))}
            </div>
          );
        }
        return (
          <p key={idx}>
            {b.lines.map((l, j) => (
              <span key={j}>{j > 0 && <br />}<Inline text={l} /></span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
